import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  InputAdornment,
  Stack,
  Checkbox,
  FormControlLabel,
  Switch,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as ImportIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useRBAC } from '../../auth/useRBAC';
import api from '../../utils/api';
import { serviceCatalogService, ServiceCatalogItem } from '../../services/serviceCatalogService';
import PricingItemDialog from '../../components/ServiceCatalog/PricingItemDialog';
import {
  withOfflineFallback,
  readClinic,
  readServiceCatalogTemplates,
  OfflineNotice,
  useIsOffline,
} from '../../offline';

interface ClinicSettings {
  showAllMedications: boolean;
  showAllVaccinations: boolean;
  showAllProcedures: boolean;
}


const ServicePricing: React.FC = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { claims, loading: claimsLoading } = useRBAC();
  const clinicId = claims?.clinicId;
  const isEditable = claims?.role === 'ADMIN' || claims?.role === 'OWNER';
  // Catalog writes go straight to the API (not executeWrite) — block them offline.
  const isOffline = useIsOffline();

  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ServiceCatalogItem | null>(null);
  const [selectedImportIds, setSelectedImportIds] = useState<string[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);

  // Fetch clinic settings for terminology toggles
  const { data: clinicSettings } = useQuery<ClinicSettings>(
    ['clinic-settings', clinicId],
    withOfflineFallback(
      async () => {
        const response = await api.get(`/clinics/${clinicId}`);
        return {
          showAllMedications: response.data.showAllMedications ?? true,
          showAllVaccinations: response.data.showAllVaccinations ?? true,
          showAllProcedures: response.data.showAllProcedures ?? true,
        };
      },
      // Read-only offline: the showAll* fields live on the synced clinic row.
      async () => {
        const c = await readClinic();
        return {
          showAllMedications: (c?.showAllMedications as boolean) ?? true,
          showAllVaccinations: (c?.showAllVaccinations as boolean) ?? true,
          showAllProcedures: (c?.showAllProcedures as boolean) ?? true,
        };
      },
    ),
    { enabled: !!clinicId, staleTime: 5 * 60 * 1000 }
  );

  const toggleMutation = useMutation(
    (data: Partial<ClinicSettings>) => api.put(`/clinics/${clinicId}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clinic-settings', clinicId]);
        queryClient.invalidateQueries(['clinic']);
        enqueueSnackbar('Display preference updated', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to update preference', { variant: 'error' });
      },
    }
  );

  const handleToggle = (field: keyof ClinicSettings) => {
    if (!clinicSettings) return;
    toggleMutation.mutate({ [field]: !clinicSettings[field] });
  };

  // Fetch service catalog items (includes both clinic and global)
  const { data: allItems = [], isLoading } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', selectedCategory],
    () => serviceCatalogService.getAll(
      selectedCategory !== 'all' ? { category: selectedCategory as any } : undefined
    )
  );

  // Fetch global template items for import dialog
  const { data: templateItems = [], isLoading: isLoadingTemplates } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog-templates'],
    withOfflineFallback(
      () => serviceCatalogService.getTemplates(),
      // The replica holds the global clinicId=null starter rows for browsing;
      // the import action itself stays online-only.
      () => readServiceCatalogTemplates() as Promise<ServiceCatalogItem[]>,
    ),
    { enabled: isImportDialogOpen }
  );

  // Show clinic-owned items + global items that haven't been overridden
  const clinicOwnedKeys = useMemo(() => {
    return new Set(
      allItems
        .filter(item => item.clinicId)
        .map(item => `${item.category}-${item.name.toLowerCase()}`)
    );
  }, [allItems]);

  const clinicItems = useMemo(() => {
    const clinicOwned = allItems.filter(item => item.clinicId);
    const globalNotOverridden = allItems.filter(
      item => !item.clinicId && item.category === 'appointment' && !clinicOwnedKeys.has(`${item.category}-${item.name.toLowerCase()}`)
    );
    return [...clinicOwned, ...globalNotOverridden]
      .filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [allItems, searchQuery, clinicOwnedKeys]);

  const paginatedClinicItems = useMemo(
    () => clinicItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [clinicItems, page, rowsPerPage],
  );

  // Reset to first page whenever filters change or the filtered list shrinks below current page
  useEffect(() => {
    setPage(0);
  }, [selectedCategory, searchQuery]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(clinicItems.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [clinicItems.length, rowsPerPage, page]);

  // Keys for all items already visible in the main table (clinic-owned + displayed global defaults)
  const visibleItemKeys = useMemo(() => {
    return new Set(clinicItems.map(item => `${item.category}-${item.name.toLowerCase()}`));
  }, [clinicItems]);

  // Filter templates that aren't already visible in the main table
  const availableTemplates = useMemo(() => {
    return templateItems.filter(item => {
      const key = `${item.category}-${item.name.toLowerCase()}`;
      return !visibleItemKeys.has(key);
    });
  }, [templateItems, visibleItemKeys]);

  const deleteMutation = useMutation(
    (id: string) => serviceCatalogService.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['service-catalog']);
        enqueueSnackbar('Pricing item deleted', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to delete item', { variant: 'error' });
      },
    }
  );

  const bulkImportMutation = useMutation(
    (itemIds: string[]) => serviceCatalogService.bulkImport(itemIds),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['service-catalog']);
        setIsImportDialogOpen(false);
        setSelectedImportIds([]);
        enqueueSnackbar(`${Array.isArray(data) ? data.length : ''} item${Array.isArray(data) && data.length === 1 ? '' : 's'} imported`, { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to import items', { variant: 'error' });
      },
    }
  );

  const handleOpenDialog = (item?: ServiceCatalogItem) => {
    setEditingItem(item ?? null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingItem(null);
  };

  const handleDelete = (item: ServiceCatalogItem) => {
    setDeleteConfirm({ id: item.id, name: item.name });
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate(deleteConfirm.id);
      setDeleteConfirm(null);
    }
  };

  const handleToggleImportItem = (id: string) => {
    setSelectedImportIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAllImport = () => {
    if (selectedImportIds.length === availableTemplates.length) {
      setSelectedImportIds([]);
    } else {
      setSelectedImportIds(availableTemplates.map(item => item.id));
    }
  };

  const handleBulkImport = () => {
    if (selectedImportIds.length > 0) {
      bulkImportMutation.mutate(selectedImportIds);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'appointment': return 'info';
      case 'medication': return 'primary';
      case 'vaccination': return 'success';
      case 'procedure': return 'warning';
      case 'other': return 'default';
      default: return 'default';
    }
  };

  // Hard gate (after all hooks): clinic pricing configuration is OWNER/ADMIN
  // only. The tab is hidden for non-admins in Settings.tsx; this covers direct
  // renders/deep links. Backend @Roles on the catalog write endpoints is the
  // real enforcement — vets still read prices through the invoice editor.
  // Spinner while claims load, so admins don't flash the denial message
  // (mirrors BillingSettings).
  if (claimsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }
  if (!isEditable) {
    return (
      <Box p={3}>
        <Typography>Only admins and owners can access service pricing.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Service Pricing
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setIsImportDialogOpen(true)}
          >
            Import Starter Items
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={isOffline}
          >
            Add Item
          </Button>
        </Stack>
      </Box>

      <OfflineNotice>
        Price-list changes need an internet connection — you can browse your prices offline.
      </OfflineNotice>

      {/* Terminology Display Preferences */}
      {clinicSettings && (
        <Paper sx={{ mb: 3, p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Clinical Record Display
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls which items appear in clinical record dropdowns. When set to "Only priced items," vets see only items from your price list, making selection faster. Priced items always appear first regardless of this setting.
          </Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={clinicSettings.showAllMedications}
                  onChange={() => handleToggle('showAllMedications')}
                  disabled={!isEditable || toggleMutation.isLoading || isOffline}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">Medications</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {clinicSettings.showAllMedications
                      ? 'ON — All medications from the medical database are shown in dropdowns'
                      : 'OFF — Only medications you\'ve added to your price list are shown'}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={clinicSettings.showAllVaccinations}
                  onChange={() => handleToggle('showAllVaccinations')}
                  disabled={!isEditable || toggleMutation.isLoading || isOffline}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">Vaccinations</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {clinicSettings.showAllVaccinations
                      ? 'ON — All vaccinations from the medical database are shown in dropdowns'
                      : 'OFF — Only vaccinations you\'ve added to your price list are shown'}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={clinicSettings.showAllProcedures}
                  onChange={() => handleToggle('showAllProcedures')}
                  disabled={!isEditable || toggleMutation.isLoading || isOffline}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">Procedures</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {clinicSettings.showAllProcedures
                      ? 'ON — All procedures from the medical database are shown in dropdowns'
                      : 'OFF — Only procedures you\'ve added to your price list are shown'}
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </Paper>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <Tabs
            value={selectedCategory}
            onChange={(_, value) => setSelectedCategory(value)}
            sx={{ flexGrow: 1 }}
          >
            <Tab label="All" value="all" />
            <Tab label="Appointments" value="appointment" />
            <Tab label="Medications" value="medication" />
            <Tab label="Vaccinations" value="vaccination" />
            <Tab label="Procedures" value="procedure" />
            <Tab label="Other" value="other" />
          </Tabs>
          <TextField
            size="small"
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
        </Stack>
      </Paper>

      {clinicItems.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Service Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Price</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedClinicItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{item.name}</Typography>
                      {!item.clinicId && (
                        <Chip label="Default" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                      )}
                    </Box>
                    {Array.isArray(item.aliases) && item.aliases.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        Also matches: {item.aliases.join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.category}
                      color={getCategoryColor(item.category) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">${item.basePrice.toFixed(2)}</TableCell>
                  <TableCell>{item.unit || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.isActive ? 'Active' : 'Inactive'}
                      color={item.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={!item.clinicId ? 'Customize price' : 'Edit'}>
                      <span>
                        <IconButton size="small" onClick={() => handleOpenDialog(item)} disabled={isOffline}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {item.clinicId && (
                      <Tooltip title="Delete">
                        <span>
                          <IconButton size="small" onClick={() => handleDelete(item)} disabled={isOffline}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={clinicItems.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </TableContainer>
      ) : !isLoading ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {selectedCategory === 'all'
              ? 'Your price list is empty'
              : `No ${selectedCategory === 'other' ? 'other' : selectedCategory} items yet`}
          </Typography>
          <Typography color="textSecondary" sx={{ mb: 3 }}>
            {selectedCategory === 'all'
              ? 'Add your services and set your own prices. You can also import starter items to get started quickly.'
              : `Add a ${selectedCategory} item or import one from the starter templates.`}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={() => setIsImportDialogOpen(true)}
            >
              Import Starter Items
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              disabled={isOffline}
            >
              Add Item
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {/* Create/Edit dialog — shared component (also used by the booking
          modal's "+ Add appointment type"); all three modes key off editingItem. */}
      <PricingItemDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        editingItem={editingItem}
        defaultCategory={selectedCategory !== 'all' ? (selectedCategory as any) : 'procedure'}
      />

      {/* Import Starter Items Dialog */}
      <Dialog open={isImportDialogOpen} onClose={() => setIsImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Starter Items</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select items to import into your price list. Imported items become fully yours to edit, rename, or delete. You can adjust prices after importing.
          </Typography>
          <OfflineNotice>
            Importing needs an internet connection — you can browse the starter items offline.
          </OfflineNotice>
          {availableTemplates.length === 0 && !isLoadingTemplates ? (
            <Alert severity="info">
              All available starter items have already been imported into your price list.
            </Alert>
          ) : (
            <>
              <Box sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedImportIds.length === availableTemplates.length && availableTemplates.length > 0}
                      indeterminate={selectedImportIds.length > 0 && selectedImportIds.length < availableTemplates.length}
                      onChange={handleSelectAllImport}
                    />
                  }
                  label={<Typography variant="body2" fontWeight="medium">Select All ({availableTemplates.length} items)</Typography>}
                />
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell sx={{ fontWeight: 'bold' }}>Service Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Price</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Unit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availableTemplates.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        onClick={() => handleToggleImportItem(item.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedImportIds.includes(item.id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.category}
                            color={getCategoryColor(item.category) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">${item.basePrice.toFixed(2)}</TableCell>
                        <TableCell>{item.unit || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsImportDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleBulkImport}
            variant="contained"
            disabled={selectedImportIds.length === 0 || bulkImportMutation.isLoading || isOffline}
          >
            {bulkImportMutation.isLoading
              ? 'Importing...'
              : `Import ${selectedImportIds.length} Item${selectedImportIds.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Item?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteConfirm?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" disabled={isOffline}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicePricing;
